import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { isNullOrUndefined } from '@deltager/organizers/admin/shared/utils/object.utils';
import { DayDate } from '@deltager/organizers/admin/core/date-format/dayDate';

export const nameof = <T>(name: keyof T): string => name.toString();

/**
 * Extracts control names with interface containing prop typeof(T) to use them as formControlName
 */
export const formGroupControlsNames = <T extends { [K in keyof T]: AbstractControl<any, any> }>(
  formGroup: FormGroup<T>,
): { [K in keyof T]: string } =>
  Object.keys(formGroup.controls).reduce(
    (acc, key) => {
      acc[key as keyof T] = nameof<T>(key as keyof T);
      return acc;
    },
    {} as { [K in keyof T]: string },
  );

/**
 * Common sort comparator for mat-table.
 * Handles string, number, and date comparisons.
 *
 * @param a The value from the first row.
 * @param b The value from the second row.
 * @param isAsc Determines ascending or descending order.
 * @returns A negative number for a < b, positive for a > b, and 0 if equal.
 */
export function commonSortComparator(a: any, b: any, isAsc: boolean): number {
  // Handle null/undefined values
  if (a == null && b == null) {
    return 0;
  }
  if (a == null) {
    return isAsc ? -1 : 1;
  }
  if (b == null) {
    return isAsc ? 1 : -1;
  }

  // Explicitly check for Date objects or parsable date strings
  if ((a instanceof Date || !isNaN(Date.parse(a))) && (b instanceof Date || !isNaN(Date.parse(b)))) {
    const dateA = a instanceof Date ? a : new Date(a);
    const dateB = b instanceof Date ? b : new Date(b);
    return (dateA.getTime() - dateB.getTime()) * (isAsc ? 1 : -1);
  }

  // Handle numbers
  if (typeof a === 'number' && typeof b === 'number') {
    return (a - b) * (isAsc ? 1 : -1);
  }

  // Handle strings (case-insensitive)
  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b) * (isAsc ? 1 : -1);
  }

  // Default comparison for other types
  return (a < b ? -1 : a > b ? 1 : 0) * (isAsc ? 1 : -1);
}

// Doesn't work with async validators
export const getControlsWithRequiredValidator = (formGroup: FormGroup): FormControl[] => {
  const requiredControls: FormControl[] = [];

  const traverseControls = (control: AbstractControl): void => {
    if (control.disabled) {
      return;
    }
    if (control instanceof FormGroup) {
      Object.values(control.controls).forEach(traverseControls);
    } else if (control instanceof FormControl) {
      const validators = control.validator ? control.validator({} as AbstractControl) : null;
      if (validators && validators['required']) {
        requiredControls.push(control);
      }
    }
  };

  traverseControls(formGroup);
  return requiredControls;
};

/**
 * Doesn't support nested objects.
 * Accepts Date time as it is and passes to BE as timezoneless
 * @param obj
 */
export const buildHttpParams = (obj: unknown): HttpParams => {
  let params = new HttpParams();

  const processNotArrayValue = (value: unknown): any => {
    if (value instanceof Date) {
      return  DayDate.toIsoWithoutTimezone(value);
    }
    return value;
  };

  const appendParam = (key: string, value: unknown) =>
    !isNullOrUndefined(value) ? params.append(key, processNotArrayValue(value)) : params;

  Object.keys(obj).forEach(key => {
    let value = obj[key];
    if (!Array.isArray(value)) {
      params = appendParam(key, processNotArrayValue(value));
      return;
    }

    value.forEach(item => {
      params = appendParam(key, processNotArrayValue(item));
    });
  });

  return params;
};

export const findNextNrFn = (values: number[]): number =>
  values?.length ? (Math.max(...values) + 1) : 0;

// Function for dev quick debug
export const getControlsWithError = (form: FormGroup) =>
  Object.keys(form.controls).map(key => form.get(key)).filter(control => control.invalid)
